import React, { useId } from 'react'
import { T } from '@transifex/react'
import { FulfilmentType } from '../../../../models/fulfilment'

export interface FulfilmentSelectProps {
  title?: string
  onTypeChange: (type: FulfilmentType) => void
  defaultType?: string
  showUnspecified?: boolean
  message?: string
  disabled?: boolean
  stretched?: boolean
  outOfStockMessage?: string
  name?: string
  /**
   * When provided, restricts the selectable fulfilment types to this list.
   * Types not in the list remain visible but are disabled (greyed out).
   * Used e.g. for new items added while continuing a fulfilment sale, which
   * may only be the sale's existing type or None (Take Now).
   */
  allowedTypes?: FulfilmentType[]
}

const fulfilmentOptions = [
  {
    label: <T _str="None" _context="Fulfilment select" />,
    trackId: 'unspecified-fulfilment',
    type: FulfilmentType.UNSPECIFIED,
  },
  {
    label: <T _str="Delivery" _context="Fulfilment select" />,
    trackId: 'delivery-fulfilment',
    type: FulfilmentType.DELIVERY,
  },
  {
    label: <T _str="Pickup" _context="Fulfilment select" />,
    trackId: 'pickup-fulfilment',
    type: FulfilmentType.PICKUP,
  },
]

// Centre the label with flexbox rather than relying on `text-align` alone, so
// the alignment survives legacy global styles in the surrounding page.
const ITEM_CONTAINER_BASE_CLASS = 'flex items-center justify-center text-center'

export const useFulfilmentSelectModel = ({
  defaultType,
  disabled,
  name,
  onTypeChange,
  showUnspecified,
  allowedTypes,
}: FulfilmentSelectProps) => {
  const fallbackFulfilmentTypeName = `fulfilType-${useId()}`
  const fulfilmentTypeName = name || fallbackFulfilmentTypeName
  const options = showUnspecified
    ? fulfilmentOptions
    : fulfilmentOptions.filter(
        option => option.type !== FulfilmentType.UNSPECIFIED
      )

  const getIsDisabled = (type: FulfilmentType) => {
    // When allowedTypes is provided, options not in the list stay visible but
    // are disabled (greyed out) rather than removed. Used e.g. for new items
    // added while continuing a fulfilment sale, where the other fulfilment
    // type is not selectable but should remain shown.
    if (allowedTypes && !allowedTypes.includes(type)) {
      return true
    }
    return defaultType !== type && disabled
  }

  return {
    fulfilmentTypeName,
    /**
     * Segmented control items overlap their left-hand neighbour by 1px (via a
     * negative left margin), so the item that comes later in the DOM paints
     * over the shared border. Disabled items use a lighter border colour,
     * which made an enabled item sitting before a disabled one (e.g. "None"
     * followed by a disabled "Delivery") render a mismatched border on that
     * one side.
     *
     * Lifting enabled items above disabled ones keeps every enabled item's
     * border consistent on all four sides. The checked item is left alone so
     * it keeps the theme's `z-20`, which must stay above everything else.
     */
    getItemContainerClass: (type: FulfilmentType) => {
      const isChecked = defaultType === type
      if (isChecked || getIsDisabled(type)) {
        return ITEM_CONTAINER_BASE_CLASS
      }
      return `${ITEM_CONTAINER_BASE_CLASS} z-10`
    },
    getIsDisabled,
    handleChange: (value: string | undefined) => {
      if (value) {
        onTypeChange(value as FulfilmentType)
      }
    },
    options,
  }
}
